import { useCallback, useState } from 'react';
import { Translate, useAuthProvider, useGetIdentity, useLogout } from 'ra-core';
import { ChevronsUpDown, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { usePublisherData } from '@/layout/publisherLayout/usePublisherData';
import { useNavigate, useLocation } from 'react-router';
import { getPublisherIdFromPath } from '@/utils';

export function UserMenu() {
  const authProvider = useAuthProvider();
  const { data: user } = useGetIdentity();
  const logout = useLogout();
  const { publishers } = usePublisherData();
  const { pathname } = useLocation();
  const publisherId = getPublisherIdFromPath(pathname);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const currentPublisher = publishers.find(p => p.id === publisherId);

  const handlePublisherChange = useCallback(
    (newPublisherId: string) => {
      if (newPublisherId && newPublisherId !== publisherId) {
        navigate(`/publisher/${newPublisherId}/offers`);
        setOpen(false);
      }
    },
    [publisherId, navigate]
  );

  if (!authProvider) return null;
  if (!user) return null;

  const avatarFallback = user.fullName?.charAt(0);
  const firstName = user.fullName?.trim().split(' ')[0];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'group/menu flex w-full items-center gap-2.5 rounded-xl p-2 text-left outline-none',
            'border border-border bg-secondary/40 shadow-premium',
            'transition-[background-color,border-color,box-shadow,transform] duration-200 ease-[var(--ease-premium)]',
            'hover:-translate-y-px hover:border-border-hover hover:bg-secondary hover:shadow-elev-2',
            'data-[state=open]:border-border-hover data-[state=open]:bg-secondary data-[state=open]:shadow-elev-2',
            'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
            'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-0 group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:shadow-none group-data-[collapsible=icon]:hover:translate-y-0 group-data-[collapsible=icon]:hover:bg-transparent group-data-[collapsible=icon]:hover:shadow-none'
          )}
        >
          <div className="relative shrink-0">
            <Avatar className="size-10 rounded-lg border border-border">
              <AvatarImage src={user.avatar} alt={user.fullName} />
              <AvatarFallback className="rounded-lg">
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            <span
              aria-hidden
              className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-emerald ring-2 ring-secondary group-data-[collapsible=icon]:ring-sidebar"
            />
          </div>
          <div className="flex min-w-0 flex-1 flex-col group-data-[collapsible=icon]:hidden">
            <span className="truncate text-sm font-semibold leading-5 tracking-tight text-sidebar-foreground">
              {firstName}
            </span>
            <span className="truncate text-xs font-medium leading-4 tracking-tight text-muted-foreground">
              {currentPublisher?.name}
            </span>
          </div>
          <ChevronsUpDown
            className={cn(
              'ml-auto size-4 shrink-0 transition-transform duration-200 ease-[var(--ease-premium)]',
              'group-data-[state=open]/menu:rotate-180',
              'group-data-[collapsible=icon]:hidden'
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={17}
        style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
        className={cn(
          'flex flex-col gap-1 rounded-xl border border-border border-t-white/10 bg-secondary/90 p-1.5 shadow-elev-3 backdrop-blur-xl',
          'data-[state=open]:zoom-in-90 data-[state=closed]:zoom-out-95',
          'data-[state=open]:duration-200 data-[state=closed]:duration-150',
          'ease-[var(--ease-premium)]'
        )}
      >
        {publishers.length > 1 &&
          publishers.map(publisher => {
            const isActive = publisher.id === publisherId;
            return (
              <DropdownMenuItem
                key={publisher.id}
                onClick={() => handlePublisherChange(publisher.id)}
                className={cn(
                  'cursor-pointer gap-2.5 rounded-lg p-1.5 focus:bg-muted',
                  isActive && 'bg-accent focus:bg-accent'
                )}
              >
                <div className="relative shrink-0">
                  <Avatar className="size-7 rounded-md border border-border">
                    <AvatarFallback className="rounded-md text-[11px] font-medium">
                      {publisher.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-emerald ring-2 ring-accent"
                    />
                  )}
                </div>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                  {publisher.name}
                </span>
              </DropdownMenuItem>
            );
          })}
        {publishers.length > 1 && <DropdownMenuSeparator />}
        <DropdownMenuItem
          variant="destructive"
          onClick={() => logout()}
          className="cursor-pointer gap-2.5 rounded-lg p-1.5"
        >
          <span className="flex size-7 shrink-0 items-center justify-center">
            <LogOut className="!text-destructive size-5" />
          </span>
          <Translate i18nKey="ra.auth.logout">Log out</Translate>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
